import React from 'react'
import PropTypes from 'prop-types'
import { Field, connect } from 'formik'

import { boolean, string } from 'yup'

import Checkbox from '../../Form/Checkbox/Checkbox'

import Fieldset from '../../Modifiers/Fieldset/Fieldset'
import { DINING_OPTION_BEHAVIORS } from '../../../utils/dining-options'
import { useFulfillment } from '../../FulfillmentProvider/DeliveryFulfillmentProvider'
import Input from '../../Form/Input/Input'
import { useFlag } from '../../FeatureFlag/use-flag'

const { TAKE_OUT } = DINING_OPTION_BEHAVIORS

const V2_FLAG = 'oo-v3-curbside-pickup-v2'

const curbsideValid = ({ fulfillmentContext, curbsidePickupConfig, featureFlags }) => {
  const { diningOptionBehavior } = fulfillmentContext
  const { enabled, enabledV2 } = curbsidePickupConfig

  return diningOptionBehavior === TAKE_OUT && ((enabledV2 && featureFlags[V2_FLAG]) || enabled)
}

export const Component = connect(({ curbsidePickupConfig, formik }) => {
  const fulfillmentContext = useFulfillment()
  const { enabledV2 } = curbsidePickupConfig || {}
  const useCurbsideV2 = useFlag('oo-v3-curbside-pickup-v2', false) && enabledV2
  const showCurbside = curbsideValid({
    curbsidePickupConfig,
    featureFlags: { 'oo-v3-curbside-pickup-v2': useCurbsideV2 },
    fulfillmentContext
  })

  return (
    showCurbside && (
      <Fieldset label='Curbside Pickup' collapsable id='curbside_fields'>
        <React.Fragment>
          <Field
            component={Checkbox}
            name='isCurbsidePickup'
            id='isCurbsidePickup'
            label={<span>Bring my order to my car</span>}
          />
          {useCurbsideV2 && formik.values.isCurbsidePickup && (
            <React.Fragment>
              <Field
                component={Input}
                name='transportDescription'
                id='transportDescription'
                label='Vehicle Make/Model*'
                type='text'
              />
              <Field
                component={Input}
                name='transportColor'
                id='transportColor'
                label='Vehicle Color'
                type='text'
              />
            </React.Fragment>
          )}
        </React.Fragment>
      </Fieldset>
    )
  )
})

Component.displayName = 'CurbsidePickup'
Component.propTypes = {
  curbsidePickupConfig: PropTypes.object.isRequired
}

export const getInitialValues = ({
  fulfillmentContext,
  curbsidePickupConfig,
  featureFlags
}) => {
  if (!curbsideValid({ fulfillmentContext, curbsidePickupConfig, featureFlags })) {
    return null
  }

  const { enabledV2 } = curbsidePickupConfig

  return {
    isCurbsidePickup: false,
    ...(featureFlags[V2_FLAG] && enabledV2 ? {
      transportDescription: '',
      transportColor: ''
    } : {})
  }
}

export const getArgsForSubmit = ({
  values,
  fulfillmentContext,
  curbsidePickupConfig,
  featureFlags
}) => {
  if (!curbsideValid({ fulfillmentContext, curbsidePickupConfig, featureFlags })) {
    return null
  }

  const { enabledV2 } = curbsidePickupConfig

  return featureFlags[V2_FLAG] && enabledV2 ? {
    curbsidePickupV2:
      values.isCurbsidePickup ? {
        transportDescription: values.transportDescription,
        transportColor: values.transportColor.trim() === '' ? null : values.transportColor
      } : null
  } : {
    curbsidePickup: {
      selected: values.isCurbsidePickup
    }
  }
}

export const getValidationSchema = ({
  curbsidePickupConfig,
  featureFlags,
  fulfillmentContext
}) => {
  if (!curbsideValid({ curbsidePickupConfig, featureFlags, fulfillmentContext })) {
    return null
  }

  const { enabledV2 } = curbsidePickupConfig

  return {
    isCurbsidePickup: boolean().default(false),
    ...(featureFlags[V2_FLAG] && enabledV2
      ? {
        transportDescription: string()
          .trim()
          .when('isCurbsidePickup', {
            is: true,
            then: string()
              .trim()
              .min(2, 'Please provide a longer description.')
              .max(100, 'Please shorten your description.')
              .required('Required'),
            otherwise: string().default('')
          }),
        transportColor: string()
          .trim()
          .when('isCurbsidePickup', {
            is: true,
            then: string()
              .trim()
              .matches(/^(.{2,20}){0,1}$/, 'Color must be between 2 and 20 letters.'),
            otherwise: string().default('')
          })
      }
      : {}
    )
  }
}
